import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, AlertTriangle, Activity, TrendingUp, 
  Users, Lock, Eye, RefreshCw, Bell, XCircle,
  CheckCircle, Clock, MapPin
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * QuantumFlowSync.AI - Security Dashboard
 * 
 * Features:
 * - Real-time security alerts via WebSocket
 * - Security metrics and statistics
 * - Recent failed login attempts
 * - Recent permission denials
 * - Live activity feed
 * - Alert severity indicators
 */

const SecurityDashboardPage = () => {
  const { apiRequest, employee } = useAuth();
  
  // State
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState({
    failed_logins_24h: 0,
    permission_denials_24h: 0,
    active_sessions: 0,
    suspicious_ips: 0
  });
  const [recentFailedLogins, setRecentFailedLogins] = useState([]);
  const [recentDenials, setRecentDenials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  
  // WebSocket ref
  const ws = useRef(null);
  
  // Fetch security metrics
  const fetchMetrics = async () => {
    try {
      const response = await apiRequest('/api/admin/audit-logs/security');
      
      if (response.success) {
        setMetrics(response.data.metrics);
        setRecentFailedLogins(response.data.recent_failed_logins || []);
        setRecentDenials(response.data.recent_denials || []);
      }
    } catch (err) {
      console.error('Failed to fetch security metrics:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Connect to WebSocket
  const connectWebSocket = () => {
    if (!employee || !employee.token) return;
    
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/security-alerts?token=${employee.token}`;
    
    ws.current = new WebSocket(wsUrl);
    
    ws.current.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
    };
    
    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'security_alert') {
        // Add alert to list
        setAlerts((prev) => [data.alert, ...prev].slice(0, 50)); // Keep last 50 alerts
        
        // Update metrics
        fetchMetrics();
        
        // Show browser notification (if permitted)
        if (Notification.permission === 'granted') {
          new Notification('Security Alert', {
            body: data.alert.description,
            icon: '/favicon.ico'
          });
        }
      }
    };
    
    ws.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnected(false);
    };
    
    ws.current.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
      
      // Reconnect after 5 seconds
      setTimeout(connectWebSocket, 5000);
    };
    
    // Send ping every 30 seconds to keep connection alive
    const pingInterval = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send('ping');
      }
    }, 30000);
    
    return () => clearInterval(pingInterval);
  };
  
  // Request notification permission
  const requestNotificationPermission = () => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };
  
  // Effect: Fetch metrics on mount
  useEffect(() => {
    fetchMetrics();
    requestNotificationPermission();
  }, []);
  
  // Effect: Connect WebSocket on mount
  useEffect(() => {
    const cleanup = connectWebSocket();
    
    return () => {
      if (ws.current) {
        ws.current.close();
      }
      if (cleanup) cleanup();
    };
  }, [employee]);
  
  // Render alert severity badge
  const renderSeverityBadge = (severity) => {
    const badges = {
      high: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: AlertTriangle,
        label: 'High'
      },
      medium: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: AlertTriangle,
        label: 'Medium'
      },
      low: {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Activity,
        label: 'Low'
      }
    };
    
    const badge = badges[severity] || badges.low;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };
  
  // Render alert type icon
  const renderAlertIcon = (alertType) => {
    const icons = {
      failed_login: XCircle,
      permission_denial: Lock,
      role_change: Users
    };
    
    const Icon = icons[alertType] || AlertTriangle;
    return <Icon className="w-5 h-5" />;
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Shield className="w-6 h-6 mr-2 text-indigo-600" />
              Security Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Real-time security monitoring and threat detection
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Connection status */}
            <div className={`flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
              connected 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-2 ${
                connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
              }`} />
              {connected ? 'Live' : 'Disconnected'}
            </div>
            
            <button
              onClick={fetchMetrics}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Failed Logins (24h)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.failed_logins_24h}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Permission Denials (24h)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.permission_denials_24h}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Lock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.active_sessions}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Suspicious IPs</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{metrics.suspicious_ips}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Alerts Feed */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Bell className="w-5 h-5 mr-2 text-indigo-600" />
              Live Security Alerts
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No security alerts</p>
                <p className="text-sm mt-1">All systems operating normally</p>
              </div>
            ) : (
              alerts.map((alert, index) => (
                <div key={index} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      alert.severity === 'high' 
                        ? 'bg-red-100 text-red-600' 
                        : alert.severity === 'medium'
                        ? 'bg-yellow-100 text-yellow-600'
                        : 'bg-blue-100 text-blue-600'
                    }`}>
                      {renderAlertIcon(alert.alert_type)}
                    </div>
                    
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {alert.description}
                        </p>
                        {renderSeverityBadge(alert.severity)}
                      </div>
                      
                      <div className="mt-2 flex items-center text-xs text-gray-500 space-x-4">
                        {alert.ip_address && (
                          <span className="flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {alert.ip_address}
                          </span>
                        )}
                        {alert.employee_email && (
                          <span className="flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            {alert.employee_email}
                          </span>
                        )}
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Just now
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        {/* Recent Failed Logins */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <XCircle className="w-5 h-5 mr-2 text-red-600" />
              Recent Failed Logins
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {recentFailedLogins.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p>No failed login attempts</p>
              </div>
            ) : (
              recentFailedLogins.map((login, index) => (
                <div key={index} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{login.employee_email}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {login.ip_address} • {new Date(login.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <span className="text-xs text-red-600 font-medium">
                      {login.attempt_count} attempts
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Recent Permission Denials */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Lock className="w-5 h-5 mr-2 text-yellow-600" />
            Recent Permission Denials
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentDenials.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                    No permission denials
                  </td>
                </tr>
              ) : (
                recentDenials.map((denial, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {denial.employee_name}
                      <div className="text-xs text-gray-500">{denial.employee_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {denial.resource}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(denial.timestamp).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {denial.ip_address}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboardPage;
